import { Controller } from "@hotwired/stimulus";

// Lazy Cal.com element-click embed. The stock snippet fetches embed.js from
// app.cal.com on every page view for every visitor; this defers that request
// until someone shows intent to book (hover/focus/touch on a [data-cal-link]
// trigger), so Cal.com never sees visitors who don't use the scheduler.
//
// Module-level state: the Cal loader lives in <head> and survives Turbo
// visits, so it must only ever be injected once per real page load.
const CAL_ORIGIN = "https://app.cal.com";
const CAL_SRC = `${CAL_ORIGIN}/embed/embed.js`;
const DEFAULT_NAMESPACE = "live-demo";
let calRequested = false;
let calReady = false;
let bookingFromSoc2Guide = false;

function ensureCalLoaded() {
  if (calRequested) return;
  calRequested = true;

  // Official Cal loader stub (queues calls until embed.js takes over).
  // prettier-ignore
  (function (C, A, L) { let p = function (a, ar) { a.q.push(ar); }; let d = C.document; C.Cal = C.Cal || function () { let cal = C.Cal; let ar = arguments; if (!cal.loaded) { cal.ns = {}; cal.q = cal.q || []; d.head.appendChild(d.createElement("script")).src = A; cal.loaded = true; } if (ar[0] === L) { const api = function () { p(api, arguments); }; const namespace = ar[1]; api.q = api.q || []; if (typeof namespace === "string") { cal.ns[namespace] = cal.ns[namespace] || api; p(cal.ns[namespace], ar); p(cal, ["initNamespace", namespace]); } else p(cal, ar); return; } p(cal, ar); }; })(window, CAL_SRC, "init");

  window.Cal("init", DEFAULT_NAMESPACE, { origin: CAL_ORIGIN });
  window.Cal.ns[DEFAULT_NAMESPACE]("ui", { hideEventTypeDetails: false, layout: "month_view" });
  window.Cal.ns[DEFAULT_NAMESPACE]("on", {
    action: "bookingSuccessfulV2",
    callback: () => {
      if (!bookingFromSoc2Guide) return;
      bookingFromSoc2Guide = false;
      // A fresh booking may still need confirmation. Never send booking data.
      window.plausible?.("SOC2 Guide Booking Created");
    },
  });

  const script = document.head.querySelector(`script[src="${CAL_SRC}"]`);
  if (script) script.addEventListener("load", () => { calReady = true; });
}

export default class extends Controller {
  connect() {
    this.warm = (event) => {
      if (event.target.closest?.("[data-cal-link]")) ensureCalLoaded();
    };
    this.click = (event) => {
      const trigger = event.target.closest?.("[data-cal-link]");
      if (!trigger) return;
      bookingFromSoc2Guide = window.location.pathname.replace(/\/$/, "") === "/soc2/common-criteria";
      ensureCalLoaded();
      // Once embed.js is running it handles [data-cal-link] clicks natively;
      // during the cold gap we queue the modal ourselves so the first click
      // still opens the scheduler when the script arrives.
      if (calReady) return;
      event.preventDefault();
      const namespace = trigger.dataset.calNamespace || DEFAULT_NAMESPACE;
      if (!window.Cal.ns[namespace]) window.Cal("init", namespace, { origin: CAL_ORIGIN });
      let config = { layout: "month_view" };
      try {
        config = { ...config, ...JSON.parse(trigger.dataset.calConfig || "{}") };
      } catch (_) {
        // Malformed data-cal-config; open with defaults.
      }
      window.Cal.ns[namespace]("modal", { calLink: trigger.dataset.calLink, config });
    };

    document.addEventListener("pointerover", this.warm, { capture: true, passive: true });
    document.addEventListener("touchstart", this.warm, { capture: true, passive: true });
    document.addEventListener("focusin", this.warm, true);
    document.addEventListener("click", this.click, true);
  }

  disconnect() {
    document.removeEventListener("pointerover", this.warm, { capture: true });
    document.removeEventListener("touchstart", this.warm, { capture: true });
    document.removeEventListener("focusin", this.warm, true);
    document.removeEventListener("click", this.click, true);
  }
}
